<template>
  <div class="details-page">
    <!-- 页面头部 -->
    <PageHeader size="page" divider>
      <template #title>
        <el-button 
          type="primary" 
          :icon="ArrowLeft" 
          @click="goBack"
          class="back-button"
        >
          返回
        </el-button>
        <span>{{ pageTitle }}</span>
      </template>
      <template #actions>
        <el-button 
          type="success" 
          :icon="Download" 
          @click="handleDownloadExcel"
          :loading="downloading"
          class="download-button"
        >
          下载Excel
        </el-button>
      </template>
    </PageHeader>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <el-skeleton :rows="5" animated />
    </div>

    <!-- 无参数提示 -->
    <div v-else-if="!route.query.id && !detailStore.isParamsValid" class="no-params-container">
      <el-empty description="缺少必要参数">
        <template #image>
          <el-icon size="64" color="#909399">
            <Warning />
          </el-icon>
        </template>
        <el-button type="primary" @click="goBack">返回列表</el-button>
      </el-empty>
    </div>

    <!-- 详情内容 -->
    <div v-else-if="detailData" class="detail-content">
      <!-- 基本信息卡片 -->
      <el-card class="info-card" shadow="hover">
        <template #header> 
          <PageHeader size="card">
            <template #title>基本信息</template>
          </PageHeader>
        </template>
        <div class="info-tags-container">
          <div class="info-tags">
            <el-tag 
              v-for="(value, key) in basicInfoItems" 
              :key="String(key)"
              :type="getTagType(String(key))"
              size="large"
              class="info-tag"
            >
              <span class="tag-label">{{ getFieldLabel(String(key)) }}：</span>
              <span class="tag-value">{{ formatFieldValue(String(key), value) }}</span>
            </el-tag>
          </div>
        </div>
      </el-card>

      <!-- 右侧粘性入口：查看经营项目楼栋图纸 -->
      <div class="drawing-entry-sticky">
        <el-tooltip content="查看经营项目楼栋图纸" placement="left">
          <el-button
            class="drawing-entry-btn"
            type="primary"
            circle
            :icon="Picture"
            :loading="operatingProjectLoading"
            :disabled="!detailOperatingProjectId"
            @click="openOperatingProjectDialog"
          />
        </el-tooltip>
      </div>

      <!-- 检测项目列表 -->
      <div v-if="detailData.检测原始数据检测项目集合 && detailData.检测原始数据检测项目集合.length > 0" class="projects-section">
        <el-card 
          v-for="(project, projectIndex) in detailData.检测原始数据检测项目集合" 
          :key="project.Id"
          class="project-card"
          shadow="hover"
        >
          <template #header>
            <PageHeader size="card">
              <template #title>{{ project.名称 }}</template>
              <template #actions>
                <el-tag type="info" size="small">项目 {{ Number(projectIndex) + 1 }}</el-tag>
              </template>
            </PageHeader>
          </template>

    

          <!-- 特殊属性 -->
          <div v-if="project.检测原始数据检测项目特殊属性集合 && project.检测原始数据检测项目特殊属性集合.length > 0" class="special-attributes">
            <div class="special-attributes-list">
              <div 
                v-for="attr in project.检测原始数据检测项目特殊属性集合" 
                :key="attr.标题"
                class="attribute-item"
              >
                <label class="attribute-label">{{ attr.标题 }}：</label>
                <el-input 
                  :model-value="attr.内容 || '无'" 
                  readonly 
                  class="attribute-input"
                />
              </div>
            </div>
          </div>


          <!-- 详细数据主表 -->
          <div v-if="project.检测原始数据详细数据主表集合 && project.检测原始数据详细数据主表集合.length > 0" class="detailed-data">
            <h4>详细数据</h4>
            <div 
              v-for="(mainData, mainIndex) in project.检测原始数据详细数据主表集合" 
              :key="mainData.Id"
              class="data-table-container"
              :data-main-table-id="getMainTableRecordId(mainData)"
            >
              <!-- 表格摘要信息 -->
              <div class="table-summary-header">
                <span class="table-title">数据表 {{ Number(mainIndex) + 1 }}</span>
                <span class="table-summary">{{ mainData.显示摘要 }}</span>
                <div v-if="getOptionExampleImages(mainData).length > 0" class="option-example-images">
                  <div
                    v-for="(optionImage, optionImageIndex) in getOptionExampleImages(mainData)"
                    :key="`${optionImage.fileName}_${optionImageIndex}`"
                    class="option-example-image"
                  >
                    <div v-if="optionImage.loading" class="option-example-placeholder">
                      <el-icon class="is-loading"><Loading /></el-icon>
                      <span>加载中</span>
                    </div>
                    <div v-else-if="optionImage.error" class="option-example-placeholder">
                      <el-icon><Picture /></el-icon>
                      <span>加载失败</span>
                    </div>
                    <el-image
                      v-else
                      :src="optionImage.url"
                      fit="contain"
                      class="option-example-thumb"
                      :preview-src-list="getOptionExampleImages(mainData).filter(img => !img.error && !img.loading && img.url).map(img => img.url)"
                      :initial-index="getOptionExampleImages(mainData).filter(img => !img.error && !img.loading && img.url).findIndex(img => img.url === optionImage.url)"
                      :preview-teleported="true"
                      :hide-on-click-modal="true"
                    >
                      <template #placeholder>
                        <div class="option-example-placeholder">
                          <el-icon class="is-loading"><Loading /></el-icon>
                        </div>
                      </template>
                      <template #error>
                        <div class="option-example-placeholder">
                          <el-icon><Picture /></el-icon>
                        </div>
                      </template>
                    </el-image>
                  </div>
                </div>
                <div class="table-summary-actions">
                  <el-button
                    type="primary"
                    size="small"
                    :loading="changeDialogLoading && changeLoadingMainTableId === String(mainData.Id || mainData.id || '')"
                    @click="openRawDataChangeDialog(mainData, project)"
                  >
                    变更
                  </el-button>
                </div>
              </div>
              
              <!-- 单条数据特殊属性（挂在每条主表数据下） -->
              <div
                v-if="mainData.检测原始数据检测项目单条数据特殊属性集合 && mainData.检测原始数据检测项目单条数据特殊属性集合.length > 0"
                class="single-special-attrs"
              >
                <el-tag
                  v-for="attr in mainData.检测原始数据检测项目单条数据特殊属性集合"
                  :key="attr.Id || attr.标题"
                  type="success"
                  size="small"
                  effect="light"
                  class="single-special-attr-tag"
                >
                  {{ attr.标题 }}：{{ attr.内容 || '无' }}
                </el-tag>
              </div>

              <!-- 数据表格 -->
              <el-table
                :data="mainData.检测原始数据详细数据子表集合"
                border
                size="small"
                class="data-table"
                scrollbar-always-on
              >
              
                <el-table-column prop="Header" label="行号" width="80" />
                <el-table-column 
                  v-for="(title, index) in project.检测原始数据检测数据格式描述.横向标题" 
                  :key="index"
                  :prop="`_${Number(index) + 1}`"
                  :label="getTransformedTitle(title, project, mainData)"
                  :width="120"
                  show-overflow-tooltip
                  :class-name="hasDividerLine(project, Number(index)) ? 'divider-column' : ''"
                />
              </el-table>

              <!-- 该主表的现场照片 -->
              <div v-if="getMainTableImages(mainData).length > 0" class="main-table-images">
                <h5>现场照片</h5>
                <div class="images-grid-small">
                  <div 
                    v-for="(image, imgIndex) in getMainTableImages(mainData)" 
                    :key="imgIndex"
                    class="image-item-small"
                  >
                    <div class="image-thumb-wrapper">
                      <!-- 加载失败的占位图 -->
                      <div v-if="image.loading" class="image-error-placeholder">
                        <el-icon class="is-loading"><Loading /></el-icon>
                        <span>加载中</span>
                      </div>
                      <div v-else-if="image.error" class="image-error-placeholder">
                        <el-icon><Picture /></el-icon>
                        <span>加载失败</span>
                      </div>
                      <!-- 正常图片 -->
                      <el-image
                        v-else
                        :src="image.url"
                        fit="cover"
                        class="thumbnail-image-small"
                        @click="openRemarkImagePreview(mainData, imgIndex)"
                      >
                        <template #error>
                          <div class="image-error-small">
                            <el-icon><Picture /></el-icon>
                          </div>
                        </template>
                        <template #placeholder>
                          <div class="image-loading-small">
                            <el-icon class="is-loading"><Loading /></el-icon>
                          </div>
                        </template>
                      </el-image>
                      <!-- 备注角标：无需点开图片即可看到备注 -->
                      <div
                        v-if="!image.loading && !image.error"
                        class="image-remark-badge"
                        :title="image.remark ? `备注：${image.remark}` : '备注：无'"
                      >
                        <span class="image-remark-badge-label">备注</span>
                        <span class="image-remark-badge-text">{{ image.remark || '无' }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </el-card>
      </div>

      <!-- 无数据提示 -->
      <el-empty v-else description="暂无检测项目数据" />
    </div>

    <!-- 错误状态 -->
    <el-empty v-else description="数据加载失败" />

    <el-dialog
      v-model="remarkImagePreviewVisible"
      title="现场照片"
      width="min(860px, 92vw)"
      top="6vh"
      destroy-on-close
    >
      <div class="remark-image-preview-dialog">
        <div class="remark-image-preview-stage">
          <el-button
            v-if="remarkPreviewImages.length > 1"
            class="remark-preview-nav remark-preview-nav--previous"
            :icon="ArrowLeft"
            circle
            :disabled="remarkPreviewIndex === 0"
            aria-label="上一张"
            @click="changeRemarkPreviewImage(-1)"
          />
          <img
            v-if="activeRemarkPreviewImage"
            :src="activeRemarkPreviewImage.url"
            class="remark-image-preview-image"
            alt="现场照片"
          />
          <el-button
            v-if="remarkPreviewImages.length > 1"
            class="remark-preview-nav remark-preview-nav--next"
            :icon="ArrowRight"
            circle
            :disabled="remarkPreviewIndex >= remarkPreviewImages.length - 1"
            aria-label="下一张"
            @click="changeRemarkPreviewImage(1)"
          />
        </div>
        <div class="remark-image-preview-note">
          <span class="remark-image-preview-note-label">备注</span>
          <span class="remark-image-preview-note-value">{{ activeRemarkPreviewImage?.remark || '无' }}</span>
        </div>
      </div>
    </el-dialog>

    <el-dialog
      v-model="operatingProjectDialogVisible"
      title="经营项目楼栋与楼层图纸"
      width="85%"
      top="5vh"
      destroy-on-close
    >
      <div v-loading="operatingProjectLoading" class="operating-project-dialog">
        <el-empty
          v-if="!operatingProjectBuildings.length"
          description="未获取到经营项目楼栋图纸数据"
        />
        <template v-else>
          <div class="op-building-tabs">
            <el-button
              v-for="(building, index) in operatingProjectBuildings"
              :key="building.id"
              :type="index === activeOperatingBuildingIndex ? 'primary' : 'default'"
              @click="handleOperatingBuildingChange(index)"
            >
              {{ building.name || `楼栋${Number(index) + 1}` }}
            </el-button>
          </div>

          <el-divider />

          <div class="op-building-meta">
            <span>楼栋名称：{{ activeOperatingBuilding?.name || '-' }}</span>
            <span>报告编号：{{ activeOperatingBuilding?.code || '-' }}</span>
            <span>地上层数：{{ activeOperatingBuilding?.floors || 0 }}</span>
            <span v-if="(activeOperatingBuilding?.basementFloors || 0) > 0">
              地下层数：{{ activeOperatingBuilding?.basementFloors }}
            </span>
          </div>

          <div class="op-drawing-layout">
            <div class="op-floor-list">
              <div
                v-for="(floorItem, index) in activeOperatingBuilding?.floorDrawings || []"
                :key="floorItem.floorNumber"
                :class="['op-floor-item', { active: index === activeOperatingFloorIndex }]"
                @click="handleOperatingFloorChange(index)"
              >
                <span>{{ floorItem.floorNumber }}层</span>
                <el-tag size="small" :type="floorItem.fileCode ? 'success' : 'info'">
                  {{ floorItem.fileCode ? '有图纸' : '无图纸' }}
                </el-tag>
              </div>
            </div>

            <div class="op-drawing-panel">
              <template v-if="activeOperatingFloor">
                <h4>{{ activeOperatingFloor.floorNumber }}层图纸</h4>
                <p class="op-drawing-code">
                  图纸编码：{{ activeOperatingFloor.fileCode || '未上传图纸' }}
                </p>

                <el-empty
                  v-if="!activeOperatingFloor.fileCode"
                  description="当前楼层未上传图纸"
                />
                <template v-else>
                  <div v-if="isPdfDrawing(activeOperatingFloor.fileCode)" class="op-pdf-preview">
                    <el-button
                      type="primary"
                      :loading="activeOperatingFloor.loading"
                      @click="loadActiveOperatingFloorFile"
                    >
                      加载PDF
                    </el-button>
                    <el-link
                      v-if="activeOperatingFloor.fileUrl"
                      :href="activeOperatingFloor.fileUrl"
                      target="_blank"
                      type="primary"
                    >
                      打开PDF预览
                    </el-link>
                    <p v-if="activeOperatingFloor.loadError" class="op-error-text">图纸加载失败，请稍后重试</p>
                  </div>

                  <div v-else class="op-image-preview">
                    <el-button
                      v-if="!activeOperatingFloor.fileUrl && !activeOperatingFloor.loading"
                      type="primary"
                      @click="loadActiveOperatingFloorFile"
                    >
                      加载图纸
                    </el-button>
                    <el-skeleton v-if="activeOperatingFloor.loading" :rows="4" animated />
                    <el-image
                      v-else-if="activeOperatingFloor.fileUrl"
                      :src="activeOperatingFloor.fileUrl"
                      class="op-floor-image"
                      fit="contain"
                      :preview-src-list="[activeOperatingFloor.fileUrl]"
                      :preview-teleported="true"
                    >
                      <template #error>
                        <div class="image-error-small">
                          <el-icon><Picture /></el-icon>
                        </div>
                      </template>
                    </el-image>
                    <p v-if="activeOperatingFloor.loadError" class="op-error-text">图纸加载失败，请稍后重试</p>
                  </div>
                </template>
              </template>
            </div>
          </div>
        </template>
      </div>
    </el-dialog>

    <el-dialog
      v-model="changeDialogVisible"
      title="检测原始数据变更"
      width="90%"
      top="20vh"
      destroy-on-close
      @closed="handleRawDataChangeDialogClosed"
    >
      <div v-loading="changeDialogLoading" class="raw-data-change-dialog-body">
        <el-empty
          v-if="!changeEditingData && !changeDialogLoading"
          description="未获取到可变更数据"
        />
        <template v-else-if="changeEditingData">
          
          <div class="raw-data-change-sub-table">
            <el-table
              :data="changeSubTableRows"
              border
              size="small"
              max-height="420"
              class="raw-data-change-table"
            >
              <el-table-column
                v-for="columnKey in changeVisibleSubTableColumns"
                :key="columnKey"
                :prop="columnKey"
                :label="getChangeSubTableColumnLabel(columnKey)"
                min-width="140"
                show-overflow-tooltip
                :class-name="getChangeSubTableColumnClassName(columnKey)"
              >
                <template #default="{ row }">
                  <span v-if="isSubTableFieldReadonly(columnKey)">{{ row[columnKey] }}</span>
                  <el-switch v-else-if="typeof row[columnKey] === 'boolean'" v-model="row[columnKey]" />
                  <el-input-number
                    v-else-if="typeof row[columnKey] === 'number'"
                    v-model="row[columnKey]"
                    :controls="false"
                    class="raw-data-change-number-input"
                  />
                  <el-input
                    v-else
                    v-model="row[columnKey]"
                    size="small"
                    clearable
                  />
                </template>
              </el-table-column>
            </el-table>
          </div>
        </template>
      </div>
      <template #footer>
        <el-button @click="changeDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="changeSubmitting" @click="submitRawDataChange">
          完成
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import PageHeader from '@/components/PageHeader.vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { ArrowLeft, ArrowRight, Warning, Download, Picture, Loading } from '@element-plus/icons-vue'
import getData, { list, ParameterSettings } from '@/network/index'
import { useDetailStore } from '@/store/detailStore'
import { getMenuPathByComponent } from '@/utils/menuRoute'
import { useRouteTitle } from '@/utils/useRouteTitle'

const route = useRoute()
const router = useRouter()
const detailStore = useDetailStore()
const pageTitle = useRouteTitle('原始记录详情', false)
const originalDetectionListPath = () => getMenuPathByComponent(
  '/RawDataManagement/OriginalDetectionList/index.vue',
  '/原始数据管理/原始检测数据列表'
)

// 设置页面标题
const setPageTitle = (title: string) => {
  document.title = title
}

// 响应式数据
const loading = ref(false)
const downloading = ref(false)
const detailData = ref<any>(null)

interface DetailImageItem {
  fileName: string
  url: string
  remark?: string
  loading?: boolean
  error?: boolean
}

interface RemarkImageSource {
  fileName: string
  remark: string
}

// 备注图像列表（按主表ID分组）
const remarkImagesByMainTable = ref<Map<string, DetailImageItem[]>>(new Map())
const optionExampleImagesByMainTable = ref<Map<string, DetailImageItem[]>>(new Map())
// 下载Excel前等待详情页现场图片全部完成，避免下载到不完整文件。
let remarkImagesLoadingPromise: Promise<void> = Promise.resolve()
const remarkImagePreviewVisible = ref(false)
const remarkPreviewImages = ref<DetailImageItem[]>([])
const remarkPreviewIndex = ref(0)
const activeRemarkPreviewImage = computed(() => remarkPreviewImages.value[remarkPreviewIndex.value])

// 获取指定主表的图片列表
const getMainTableImages = (mainData: any) => {
  const mainTableId = mainData.Id || mainData.id
  return remarkImagesByMainTable.value.get(String(mainTableId)) || []
}

const openRemarkImagePreview = (mainData: any, imageIndex: number) => {
  const sourceImages = getMainTableImages(mainData)
  const targetImage = sourceImages[imageIndex]
  const previewImages = sourceImages.filter(image => !image.loading && !image.error && image.url)
  const previewIndex = previewImages.findIndex(image => image === targetImage)
  if (previewIndex < 0) return

  remarkPreviewImages.value = previewImages
  remarkPreviewIndex.value = previewIndex
  remarkImagePreviewVisible.value = true
}

const changeRemarkPreviewImage = (offset: number) => {
  const nextIndex = remarkPreviewIndex.value + offset
  if (nextIndex >= 0 && nextIndex < remarkPreviewImages.value.length) {
    remarkPreviewIndex.value = nextIndex
  }
}

const getOptionExampleImages = (mainData: any) => {
  const mainTableId = mainData.Id || mainData.id
  return optionExampleImagesByMainTable.value.get(String(mainTableId)) || []
}

interface OperatingFloorDrawing {
  floorNumber: number
  fileCode: string
  fileUrl: string
  loading: boolean
  loadError: boolean
}

interface OperatingBuilding {
  id: string
  name: string
  code: string
  floors: number // 地上层数（与楼栋信息维护页一致，层数字段只代表地上层数）
  basementFloors: number // 地下层数（0 表示无地下层）
  floorDrawings: OperatingFloorDrawing[]
}

const operatingProjectDialogVisible = ref(false)
const operatingProjectLoading = ref(false)
const operatingProjectDetailId = ref('')
const operatingProjectBuildings = ref<OperatingBuilding[]>([])
const activeOperatingBuildingIndex = ref(0)
const activeOperatingFloorIndex = ref(0)
const operatingProjectFileCache = new Map<string, string>()

const CHANGE_SUB_TABLE_KEY = '检测原始数据详细数据子表集合'
const RAW_DATA_CHANGE_READONLY_FIELDS = new Set(['Id'])
const RAW_DATA_CHANGE_SUB_TABLE_READONLY_FIELDS = new Set(['Id', '检测原始数据详细数据主表id'])

const changeDialogVisible = ref(false)
const changeDialogLoading = ref(false)
const changeSubmitting = ref(false)
const changeLoadingMainTableId = ref('')
const changeQueryId = ref('')
const changeEditingData = ref<Record<string, any> | null>(null)
const changeComplexFieldTexts = ref<Record<string, string>>({})
const changeSourceProject = ref<Record<string, any> | null>(null)
const changeSourceMainData = ref<Record<string, any> | null>(null)
const changeOpenedMainTableId = ref('')

const CHANGE_POSITION_STORAGE_KEY = 'original-detection-change-position'

interface ChangePositionCache {
  detailId: string
  mainTableId: string
  savedAt: number
}

const toNonEmptyString = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  const text = String(value).trim()
  if (!text || text === 'null' || text === 'undefined') return ''
  return text
}

const normalizeRemarkImage = (source: unknown): RemarkImageSource | null => {
  if (typeof source === 'string') {
    const fileName = toNonEmptyString(source)
    return fileName ? { fileName, remark: '' } : null
  }

  if (Array.isArray(source)) {
    const fileName = toNonEmptyString(source[0])
    return fileName ? { fileName, remark: toNonEmptyString(source[1]) } : null
  }

  if (!source || typeof source !== 'object') return null
  const item = source as Record<string, unknown>
  const fileName = toNonEmptyString(
    item['文件名'] ?? item['fileName'] ?? item['名称'] ?? item['name'] ?? item['图片名称'] ?? item['图片']
  )
  if (!fileName) return null
  return {
    fileName,
    remark: toNonEmptyString(item['备注'] ?? item['remark'] ?? item['说明'] ?? item['内容'])
  }
}

const parseRemarkImages = (source: unknown): RemarkImageSource[] => {
  if (!Array.isArray(source)) return []
  return source.map(normalizeRemarkImage).filter((item): item is RemarkImageSource => item !== null)
}

const toPositiveInt = (value: unknown, fallback: number): number => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback
  return Math.floor(parsed)
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const getMainTableRecordId = (mainData: Record<string, unknown> | null | undefined): string => {
  if (!mainData) return ''
  return toNonEmptyString(mainData.Id ?? mainData.id)
}

const getCurrentDetailId = (): string => {
  if (detailStore.isParamsValid) return detailStore.getDetailId
  return toNonEmptyString(route.query.id)
}

const saveChangePositionCache = (detailId: string, mainTableId: string) => {
  if (!detailId || !mainTableId) return
  const payload: ChangePositionCache = {
    detailId,
    mainTableId,
    savedAt: Date.now()
  }
  sessionStorage.setItem(CHANGE_POSITION_STORAGE_KEY, JSON.stringify(payload))
}

const readChangePositionCache = (): ChangePositionCache | null => {
  const text = sessionStorage.getItem(CHANGE_POSITION_STORAGE_KEY)
  if (!text) return null
  try {
    const parsed = JSON.parse(text) as ChangePositionCache
    if (!parsed?.detailId || !parsed?.mainTableId) return null
    return parsed
  } catch {
    return null
  }
}

const clearChangePositionCache = () => {
  sessionStorage.removeItem(CHANGE_POSITION_STORAGE_KEY)
}

const scrollToMainTablePosition = (mainTableId: string): boolean => {
  if (!mainTableId) return false
  const target = document.querySelector<HTMLElement>(`.data-table-container[data-main-table-id="${mainTableId}"]`)
  if (!target) return false
  target.scrollIntoView({ behavior: 'smooth', block: 'start' })
  return true
}

const restoreChangePositionIfNeeded = async (detailId: string) => {
  const cached = readChangePositionCache()
  if (!cached) return
  if (cached.detailId !== detailId) return

  await nextTick()
  for (let i = 0; i < 12; i += 1) {
    await sleep(150)
    await nextTick()
    if (scrollToMainTablePosition(cached.mainTableId)) {
      clearChangePositionCache()
      return
    }
  }
}

function deepClone<T>(value: T): T {
  try {
    return JSON.parse(JSON.stringify(value)) as T
  } catch {
    return value
  }
}

const isComplexValue = (value: unknown): boolean => {
  return value !== null && typeof value === 'object'
}

const getOperatingProjectId = (source: Record<string, unknown> | null | undefined): string => {
  if (!source) return ''
  const idValue =
    source['经营项目id'] ??
    source['经营项目ID'] ??
    source.operatingProjectId ??
    source.businessProjectId
  return toNonEmptyString(idValue)
}

const detailBuildingList = computed(() => {
  const buildingList = detailData.value?.建筑列表
  return Array.isArray(buildingList) ? (buildingList as Array<Record<string, unknown>>) : []
})

const findBuildingForMainTable = (mainData: Record<string, unknown> | null | undefined) => {
  if (!mainData) return null
  if (!detailBuildingList.value.length) return null

  const mainBuildingIdCandidates = [
    mainData['建筑ID'],
    mainData['建筑id'],
    mainData['建筑Id'],
    mainData['楼栋ID'],
    mainData['楼栋id'],
    mainData['楼栋Id']
  ].map(toNonEmptyString).filter(Boolean)

  if (mainBuildingIdCandidates.length > 0) {
    const matchedById = detailBuildingList.value.find((building) => {
      const buildingId = toNonEmptyString(building['Id'] ?? building['id'])
      return !!buildingId && mainBuildingIdCandidates.includes(buildingId)
    })
    if (matchedById) return matchedById
  }

  const mainBuildingName = toNonEmptyString(mainData['建筑名称'] ?? mainData['楼栋名称'])
  if (mainBuildingName) {
    const matchedByName = detailBuildingList.value.find((building) => {
      return toNonEmptyString(building['名称']) === mainBuildingName
    })
    if (matchedByName) return matchedByName
  }

  return null
}

const getRawDataChangeQueryId = (mainData: Record<string, unknown> | null | undefined): string => {
  const building = findBuildingForMainTable(mainData)
  if (building) {
    const buildingProjectId = toNonEmptyString(building['Id'] ?? building['Id'])
    if (buildingProjectId) return buildingProjectId
  }
  return detailOperatingProjectId.value
}

const buildOperatingProjectBuildings = (detail: Record<string, unknown> | null | undefined): OperatingBuilding[] => {
  if (!detail) return []
  const buildingList = Array.isArray(detail['建筑列表']) ? (detail['建筑列表'] as Array<Record<string, unknown>>) : []

  return buildingList.map((building, index) => {
    const drawingPackage = Array.isArray(building['图纸包']) ? (building['图纸包'] as Array<Record<string, unknown>>) : []
    // 与楼栋信息维护页一致：层数只代表地上层数，地下层数单独记录
    const aboveGroundFloors = toPositiveInt(building['层数'], 0)
    const basementFloors = toPositiveInt(building['地下层数'], 0)

    // 完整楼层号（升序，地下在前、地上在后），如 地下2层 + 地上3层 => [-2, -1, 1, 2, 3]
    let floorNumbers: number[] = []
    for (let i = basementFloors; i >= 1; i--) floorNumbers.push(-i)
    for (let i = 1; i <= aboveGroundFloors; i++) floorNumbers.push(i)

    // 兼容旧数据：没有地下层数、甚至缺少层数字段时，按图纸包行数兜底生成地上楼层
    if (floorNumbers.length === 0) {
      floorNumbers = Array.from({ length: Math.max(1, drawingPackage.length) }, (_, i) => i + 1)
    }

    const floorToDrawingRow = new Map<number, Record<string, unknown>>()
    const untaggedRows: Array<Record<string, unknown>> = []

    // 优先按图纸自身的“层数”字段归属楼层（可为 -1/-2 等地下层）；
    // 没有层数标记的图纸（旧数据）保留原顺序，在后面按“最小未占用楼层”补位
    for (const row of drawingPackage) {
      const rawFloor = row?.['层数']
      const taggedFloor = rawFloor === null || rawFloor === undefined || rawFloor === '' ? Number.NaN : Number(rawFloor)
      if (Number.isFinite(taggedFloor) && floorNumbers.includes(taggedFloor) && !floorToDrawingRow.has(taggedFloor)) {
        floorToDrawingRow.set(taggedFloor, row)
      } else {
        untaggedRows.push(row)
      }
    }

    const floorDrawings: OperatingFloorDrawing[] = floorNumbers.map(floorNumber => {
      const drawingRow = floorToDrawingRow.get(floorNumber) || untaggedRows.shift() || {}
      const fileCode = toNonEmptyString(
        drawingRow['内容'] ??
        drawingRow['fileName'] ??
        drawingRow['fileCode']
      )
      return {
        floorNumber,
        fileCode,
        fileUrl: '',
        loading: false,
        loadError: false
      }
    })

    return {
      id: toNonEmptyString(building.Id ?? building.id) || `building-${index + 1}`,
      name: toNonEmptyString(building['名称']) || `${index + 1}号楼`,
      code: toNonEmptyString(building['报告编号'] ?? building['编码']),
      floors: aboveGroundFloors,
      basementFloors,
      floorDrawings
    }
  })
}

const clearOperatingProjectFileCache = () => {
  for (const url of operatingProjectFileCache.values()) {
    if (url.startsWith('blob:')) {
      URL.revokeObjectURL(url)
    }
  }
  operatingProjectFileCache.clear()
}

const parseDownloadResponseToUrl = (res: any): string => {
  if (res instanceof Blob) {
    return URL.createObjectURL(res)
  }

  const data = res?.response ?? res?.data ?? res
  if (data instanceof Blob) {
    return URL.createObjectURL(data)
  }

  if (typeof data === 'string') {
    if (data.startsWith('blob:') || data.startsWith('data:') || data.startsWith('http')) {
      return data
    }
    return `data:image/jpeg;base64,${data}`
  }

  return ''
}

const isPdfDrawing = (fileCode: string): boolean => /\.pdf($|\?)/i.test(fileCode)

const detailOperatingProjectId = computed(() => getOperatingProjectId(detailData.value))

const changeTopLevelFieldKeys = computed(() => {
  if (!changeEditingData.value) return []
  return Object.keys(changeEditingData.value).filter((fieldKey) => fieldKey !== CHANGE_SUB_TABLE_KEY)
})

const changeSubTableRows = computed(() => {
  if (!changeEditingData.value) return []
  const rows = changeEditingData.value[CHANGE_SUB_TABLE_KEY]
  return Array.isArray(rows) ? (rows as Array<Record<string, any>>) : []
})

const changeFormatHorizontalHeaders = computed(() => {
  const formatDesc = changeSourceProject.value?.检测原始数据检测数据格式描述
  const headers = formatDesc?.横向标题
  return Array.isArray(headers) ? (headers as string[]) : []
})

const changeVisibleSubTableColumns = computed(() => {
  if (changeFormatHorizontalHeaders.value.length > 0) {
    return ['Header', ...changeFormatHorizontalHeaders.value.map((_, index) => `_${index + 1}`)]
  }
  return changeSubTableColumns.value
})

const getSubTableColumnWeight = (key: string) => {
  if (key === 'Header') return 0
  const numberedColumnMatch = key.match(/^_(\d+)$/)
  if (numberedColumnMatch) return Number(numberedColumnMatch[1])
  if (key === 'ISDELETED') return 900
  if (key === '检测原始数据详细数据主表id') return 901
  if (key === 'Id') return 902
  return 500
}

const changeSubTableColumns = computed(() => {
  const columnSet = new Set<string>()
  for (const row of changeSubTableRows.value) {
    Object.keys(row).forEach((key) => columnSet.add(key))
  }

  return Array.from(columnSet).sort((a, b) => {
    const weightA = getSubTableColumnWeight(a)
    const weightB = getSubTableColumnWeight(b)
    if (weightA !== weightB) return weightA - weightB
    return a.localeCompare(b)
  })
})

const getChangeSubTableColumnLabel = (columnKey: string) => {
  if (columnKey === 'Header') return '行号'
  const match = columnKey.match(/^_(\d+)$/)
  if (match && changeFormatHorizontalHeaders.value.length > 0) {
    const columnIndex = Number(match[1]) - 1
    const title = changeFormatHorizontalHeaders.value[columnIndex]
    if (title) {
      return getTransformedTitle(
        title,
        changeSourceProject.value,
        changeEditingData.value || changeSourceMainData.value
      )
    }
  }
  return columnKey
}

const getChangeSubTableColumnClassName = (columnKey: string) => {
  const match = columnKey.match(/^_(\d+)$/)
  if (!match || !changeSourceProject.value) return ''
  const columnIndex = Number(match[1]) - 1
  return hasDividerLine(changeSourceProject.value, columnIndex) ? 'divider-column' : ''
}

const initChangeComplexFieldTexts = () => {
  const next: Record<string, string> = {}
  if (!changeEditingData.value) {
    changeComplexFieldTexts.value = next
    return
  }

  for (const fieldKey of changeTopLevelFieldKeys.value) {
    const fieldValue = changeEditingData.value[fieldKey]
    if (isComplexValue(fieldValue)) {
      next[fieldKey] = JSON.stringify(fieldValue, null, 2)
    }
  }
  changeComplexFieldTexts.value = next
}

const isChangeFieldComplex = (fieldKey: string): boolean => {
  if (!changeEditingData.value) return false
  return isComplexValue(changeEditingData.value[fieldKey])
}

const isChangeFieldReadonly = (fieldKey: string): boolean => {
  return RAW_DATA_CHANGE_READONLY_FIELDS.has(fieldKey)
}

const isSubTableFieldReadonly = (fieldKey: string): boolean => {
  return RAW_DATA_CHANGE_SUB_TABLE_READONLY_FIELDS.has(fieldKey)
}

const applyChangeComplexField = (fieldKey: string): boolean => {
  if (!changeEditingData.value) return false
  if (!(fieldKey in changeComplexFieldTexts.value)) return true

  const raw = changeComplexFieldTexts.value[fieldKey]
  if (raw === undefined || raw === null || raw.trim() === '') {
    changeEditingData.value[fieldKey] = null
    return true
  }

  try {
    changeEditingData.value[fieldKey] = JSON.parse(raw)
    return true
  } catch {
    ElMessage.error(`${fieldKey} 不是合法JSON，请修正后再提交`)
    return false
  }
}

const syncAllComplexFields = (): boolean => {
  for (const fieldKey of Object.keys(changeComplexFieldTexts.value)) {
    if (!applyChangeComplexField(fieldKey)) return false
  }
  return true
}

const handleRawDataChangeDialogClosed = () => {
  changeEditingData.value = null
  changeComplexFieldTexts.value = {}
  changeDialogLoading.value = false
  changeSubmitting.value = false
  changeLoadingMainTableId.value = ''
  changeQueryId.value = ''
  changeSourceProject.value = null
  changeSourceMainData.value = null
  changeOpenedMainTableId.value = ''
}

const activeOperatingBuilding = computed(() => {
  return operatingProjectBuildings.value[activeOperatingBuildingIndex.value] || null
})

const activeOperatingFloor = computed(() => {
  const building = activeOperatingBuilding.value
  if (!building) return null
  return building.floorDrawings[activeOperatingFloorIndex.value] || null
})

const loadOperatingFloorFile = async (floor: OperatingFloorDrawing | null) => {
  if (!floor || !floor.fileCode) return
  if (floor.loading) return

  if (operatingProjectFileCache.has(floor.fileCode)) {
    floor.fileUrl = operatingProjectFileCache.get(floor.fileCode) || ''
    floor.loadError = false
    return
  }

  floor.loading = true
  floor.loadError = false
  try {
    const res = await ParameterSettings.operatingProjectDownload({ fileName: floor.fileCode })
    const fileUrl = parseDownloadResponseToUrl(res)
    if (!fileUrl) {
      floor.loadError = true
      return
    }
    floor.fileUrl = fileUrl
    operatingProjectFileCache.set(floor.fileCode, fileUrl)
  } catch (error) {
    console.error(`加载经营项目图纸失败: ${floor.fileCode}`, error)
    floor.loadError = true
  } finally {
    floor.loading = false
  }
}

const loadActiveOperatingFloorFile = async () => {
  await loadOperatingFloorFile(activeOperatingFloor.value)
}

const handleOperatingBuildingChange = async (index: number) => {
  activeOperatingBuildingIndex.value = index
  activeOperatingFloorIndex.value = 0
  await loadActiveOperatingFloorFile()
}

const handleOperatingFloorChange = async (index: number) => {
  activeOperatingFloorIndex.value = index
  await loadActiveOperatingFloorFile()
}

const loadOperatingProjectDefinition = async (operatingProjectId: string) => {
  if (!operatingProjectId) {
    operatingProjectDetailId.value = ''
    operatingProjectBuildings.value = []
    return
  }

  try {
    operatingProjectLoading.value = true
    const res = await ParameterSettings.operatingProjectById({ id: operatingProjectId })

    if (res && (res.status === 200 || res.status === 0) && res.success && res.response) {
      clearOperatingProjectFileCache()
      operatingProjectDetailId.value = operatingProjectId
      operatingProjectBuildings.value = buildOperatingProjectBuildings(res.response)
      activeOperatingBuildingIndex.value = 0
      activeOperatingFloorIndex.value = 0
      return
    }

    operatingProjectDetailId.value = operatingProjectId
    operatingProjectBuildings.value = []
  } catch (error) {
    console.error('加载经营项目定义失败:', error)
    operatingProjectDetailId.value = operatingProjectId
    operatingProjectBuildings.value = []
  } finally {
    operatingProjectLoading.value = false
  }
}

const openOperatingProjectDialog = async () => {
  const operatingProjectId = detailOperatingProjectId.value
  if (!operatingProjectId) {
    ElMessage.warning('当前检测数据缺少经营项目ID')
    return
  }

  operatingProjectDialogVisible.value = true

  if (operatingProjectDetailId.value !== operatingProjectId || operatingProjectBuildings.value.length === 0) {
    await loadOperatingProjectDefinition(operatingProjectId)
  }

  await loadActiveOperatingFloorFile()
}

const openRawDataChangeDialog = async (
  mainData: Record<string, unknown>,
  project: Record<string, unknown>
) => {
  const queryId = getRawDataChangeQueryId(mainData)
  if (!queryId) {
    ElMessage.warning('当前数据表未匹配到ID，无法变更')
    return
  }

  const mainTableId = getMainTableRecordId(mainData)
  changeLoadingMainTableId.value = mainTableId
  changeOpenedMainTableId.value = mainTableId
  changeDialogVisible.value = true
  changeDialogLoading.value = true
  changeEditingData.value = null
  changeQueryId.value = queryId
  changeComplexFieldTexts.value = {}
  changeSourceProject.value = deepClone(project)
  changeSourceMainData.value = deepClone(mainData)

  try {
    const res = await list.rawDataChangeById({ id: mainTableId })
    if (res && (res.status === 200 || res.status === 0) && res.success && res.response) {
      changeEditingData.value = deepClone(res.response)
      initChangeComplexFieldTexts()
      return
    }
    ElMessage.error(res?.msg || '获取变更数据失败')
    changeDialogVisible.value = false
  } catch (error) {
    console.error('获取检测原始数据变更详情失败:', error)
    ElMessage.error('获取变更数据失败')
    changeDialogVisible.value = false
  } finally {
    changeDialogLoading.value = false
    changeLoadingMainTableId.value = ''
  }
}

const submitRawDataChange = async () => {
  if (!changeEditingData.value) {
    ElMessage.warning('暂无可提交的变更数据')
    return
  }

  if (!syncAllComplexFields()) return

  try {
    changeSubmitting.value = true
    const payload = deepClone(changeEditingData.value)
    const res = await list.rawDataChangeSubmit(payload)

    if (res && (res.status === 200 || res.status === 0) && res.success) {
      const detailId = getCurrentDetailId()
      const mainTableIdToCache = changeOpenedMainTableId.value || getMainTableRecordId(changeSourceMainData.value)
      saveChangePositionCache(detailId, mainTableIdToCache)
      ElMessage.success('变更完成')
      changeDialogVisible.value = false
      await loadDetailData({ restorePosition: true })
      return
    }

    ElMessage.error(res?.msg || '变更失败')
  } catch (error) {
    console.error('提交检测原始数据变更失败:', error)
    ElMessage.error('变更失败，请稍后重试')
  } finally {
    changeSubmitting.value = false
  }
}

// 计算属性：基本信息项（动态获取所有非空字段）
const basicInfoItems = computed(() => {
  if (!detailData.value) return {}
  
  const items: any = {}
  // 系统字段，不显示在基本信息中
  const systemFields = ['Id', 'ISDELETED', '检测原始数据检测项目集合', 'id']
  
  for (const key in detailData.value) {
    // 跳过系统字段、包含id的字段和空值
    if (!systemFields.includes(key) && 
        !key.toLowerCase().includes('id') &&
        detailData.value[key] !== undefined && 
        detailData.value[key] !== null && 
        detailData.value[key] !== '') {
      items[key] = detailData.value[key]
    }
  }
  return items
})

// 获取字段标签（智能生成）
const getFieldLabel = (key: string) => {
  // 特殊字段映射
  const specialMappings: Record<string, string> = {
    '经营项目id': '经营项目ID',
    'ISDELETED': '是否删除',
    'Id': 'ID'
  }
  
  if (specialMappings[key]) {
    return specialMappings[key]
  }
  
  // 智能转换规则
  let label = key
  
  // 处理下划线命名
  if (key.includes('_')) {
    label = key.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ')
  }
  
  // 处理驼峰命名
  if (key.match(/[a-z][A-Z]/)) {
    label = key.replace(/([a-z])([A-Z])/g, '$1 $2')
  }
  
  // 处理ID字段
  if (key.toLowerCase().endsWith('id')) {
    label = key.replace(/id$/i, 'ID')
  }
  
  return label
}

// 获取标签类型（根据字段名动态判断）
const getTagType = (key: string) => {
  if (key.includes('时间') || key.includes('Time') || key.includes('Date')) {
    return 'info'
  } else if (key.includes('编码') || key.includes('Code') || key.includes('ID') || key.includes('id')) {
    return 'primary'
  } else if (key.includes('名称') || key.includes('Name') || key.includes('Title')) {
    return 'success'
  } else if (key.includes('状态') || key.includes('Status') || key.includes('State')) {
    return 'warning'
  }
  return 'info'
}

// 格式化字段值
const formatFieldValue = (key: string, value: any) => {
  if (key.includes('时间') || key.includes('Time') || key.includes('Date')) {
    return formatDateTime(value)
  }
  return value || '-'
}

// 格式化时间显示
const formatDateTime = (dateTimeStr: string) => {
  if (!dateTimeStr || dateTimeStr === '0001-01-01 00:00:00') {
    return '-'
  }
  try {
    const date = new Date(dateTimeStr)
    if (isNaN(date.getTime())) {
      return '-'
    }
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  } catch (error) {
    return '-'
  }
}

// 转换横向标题，将@@1~4转换为轴线1~4的属性值
const getTransformedTitle = (title: string, project: any, mainData: any) => {
  // 检查是否包含@@1~4的模式（如@@2的高度）
  const match = title.match(/@@([1-4])/)
  if (match) {
    const axisNumber = match[1]
    const axisKey = `轴线${axisNumber}`
    
    // 首先在mainData中查找轴线属性
    if (mainData && mainData[axisKey]) {
      const axisValue = mainData[axisKey]
      // 如果轴线值为"0"，返回原始标题
      if (axisValue === "0" || axisValue === 0) {
        return title
      }
      // 将@@数字替换为轴线属性值
      return title.replace(/@@([1-4])/, axisValue)
    }
    
    // 如果mainData中没有，再在特殊属性集合中查找
    if (project.检测原始数据检测项目特殊属性集合) {
      const axisAttr = project.检测原始数据检测项目特殊属性集合.find((attr: any) => 
        attr.标题 === axisKey
      )
      if (axisAttr && axisAttr.内容) {
        const axisValue = axisAttr.内容
        // 如果轴线值为"0"，返回原始标题
        if (axisValue === "0" || axisValue === 0) {
          return title
        }
        // 将@@数字替换为轴线属性值
        return title.replace(/@@([1-4])/, axisValue)
      }
    }
    
    // 如果都没找到，返回原始标题
    return title
  }
  
  // 如果不包含@@1~4格式，返回原始标题
  return title
}

// 判断是否有分区线
const hasDividerLine = (project: any, index: number) => {
  const formatDesc = project.检测原始数据检测数据格式描述
  if (formatDesc && formatDesc.分区线 && Array.isArray(formatDesc.分区线)) {
    return formatDesc.分区线[index] === true
  }
  return false
}

const getExcelColumnLetter = (columnNumber: number): string => {
  let result = ''
  let value = columnNumber
  while (value > 0) {
    const remainder = (value - 1) % 26
    result = String.fromCharCode(65 + remainder) + result
    value = Math.floor((value - 1) / 26)
  }
  return result
}

interface ExcelImageGroup {
  summary: string
  images: DetailImageItem[]
}

const getRemarkImageGroups = (): ExcelImageGroup[] => {
  const projects = detailData.value?.检测原始数据检测项目集合
  if (!Array.isArray(projects)) return []

  const groups: ExcelImageGroup[] = []
  for (const project of projects) {
    const mainTables = project?.检测原始数据详细数据主表集合
    if (!Array.isArray(mainTables)) continue

    for (const mainData of mainTables) {
      const mainTableId = String(mainData.Id || mainData.id)
      const images = remarkImagesByMainTable.value.get(mainTableId) || []
      if (images.length > 0) {
        groups.push({
          summary: String(mainData.显示摘要 || '未命名数据表'),
          images
        })
      }
    }
  }
  return groups
}

const getExcelImageExtension = (blob: Blob, fileName: string): 'jpeg' | 'png' | 'gif' => {
  const mime = blob.type.toLowerCase()
  if (mime.includes('png') || /\.png$/i.test(fileName)) return 'png'
  if (mime.includes('gif') || /\.gif$/i.test(fileName)) return 'gif'
  return 'jpeg'
}

// 后端 Excel 不包含页面现场照片，这里在保留原始工作表的基础上追加照片工作表。
const appendRemarkImagesToWorkbook = async (sourceBlob: Blob, groups: ExcelImageGroup[]): Promise<Blob> => {
  const excelJsModule = await import('exceljs')
  const ExcelJS = excelJsModule.default || excelJsModule
  const workbook = new ExcelJS.Workbook()
  await workbook.xlsx.load(await sourceBlob.arrayBuffer())

  const worksheetName = workbook.getWorksheet('现场照片') ? '现场照片_1' : '现场照片'
  const worksheet = workbook.addWorksheet(worksheetName)
  worksheet.getColumn(1).width = 80

  const maxImageCount = Math.max(...groups.map(group => group.images.length), 0)
  for (let imageIndex = 0; imageIndex < maxImageCount; imageIndex++) {
    worksheet.getColumn(imageIndex * 2 + 2).width = 18
    worksheet.getColumn(imageIndex * 2 + 3).width = 26
  }

  let currentRow = 1
  for (const group of groups) {
    const titleCell = worksheet.getCell(`A${currentRow}`)
    titleCell.value = group.summary
    titleCell.font = { bold: true, size: 12 }
    titleCell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true }
    worksheet.getRow(currentRow).height = 90

    for (let imageIndex = 0; imageIndex < group.images.length; imageIndex++) {
      const image = group.images[imageIndex]
      const imageColumn = imageIndex * 2 + 2
      const remarkColumnLetter = getExcelColumnLetter(imageColumn + 1)
      const remarkCell = worksheet.getCell(`${remarkColumnLetter}${currentRow}`)
      remarkCell.value = `备注：${image.remark?.trim() || '无'}`
      remarkCell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true }

      const imageResponse = await fetch(image.url)
      if (!imageResponse.ok) {
        throw new Error(`现场图片下载失败：${image.fileName}`)
      }
      const imageBlob = await imageResponse.blob()
      const imageId = workbook.addImage({
        buffer: await imageBlob.arrayBuffer(),
        extension: getExcelImageExtension(imageBlob, image.fileName)
      })

      worksheet.addImage(imageId, {
        tl: { col: imageColumn - 1, row: currentRow - 1 },
        ext: { width: 120, height: 80 }
      })
    }

    currentRow++
  }

  const buffer = await workbook.xlsx.writeBuffer()
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })
}

// 下载Excel
const handleDownloadExcel = async () => {
  try {
    downloading.value = true

    const imageItems = Array.from(remarkImagesByMainTable.value.values()).flat()
    if (imageItems.some((image) => image.loading)) {
      ElMessage.info('现场图片仍在加载，已等待全部图片加载完成后继续下载')
    }

    await remarkImagesLoadingPromise

    const failedImages = Array.from(remarkImagesByMainTable.value.values())
      .flat()
      .filter((image) => image.error || !image.url)
    if (failedImages.length > 0) {
      ElMessage.warning(`有 ${failedImages.length} 张现场图片加载失败，已停止下载，请刷新后重试`)
      return
    }
    
    // 获取当前数据的ID
    let id = ''
    if (detailStore.isParamsValid) {
      id = detailStore.getDetailId
    } else {
      id = route.query.id as string
    }
    
    if (!id) {
      ElMessage.error('缺少ID参数，无法下载')
      return
    }
    
    const res = await list.downloadExcel({ id })

    // 后端文件作为基础 Excel，现场照片由详情页加载后追加到工作簿。
    const sourceBlob = res instanceof Blob
      ? res
      : new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const imageGroups = getRemarkImageGroups()
    const blob = imageGroups.length > 0
      ? await appendRemarkImagesToWorkbook(sourceBlob, imageGroups)
      : sourceBlob

    // 创建Blob对象并下载
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    // 使用数据名称作为文件名，如果没有则使用默认名称
    const fileName = detailData.value?.名称 ? `${detailData.value.名称}.xlsx` : '检测原始数据.xlsx'
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    
    ElMessage.success('下载成功')
  } catch (error) {
    console.error('下载Excel失败:', error)
    ElMessage.error('下载失败，请重试')
  } finally {
    downloading.value = false
  }
}

// 返回列表
const goBack = () => {
  // 先取消进行中的图片加载，避免大量图片解码/请求占满主线程导致返回点击延迟响应
  cancelImageLoading()
  // 如果store中有来源路径，则返回到来源路径，否则使用浏览器后退
  const fromPath = detailStore.getFromPath
  if (fromPath) {
    router.push(fromPath)
  } else {
    router.go(-1)
  }
}

// 加载备注图像（按主表分组）
// ========== 现场照片本地缓存（IndexedDB + 浏览器会话标记：关闭浏览器自动清空） ==========
// 说明：图片以 Blob 原样缓存在 IndexedDB，容量大、无 base64 膨胀，手机拍摄的大图也能缓存；
// 通过 session cookie 判断浏览器会话，关闭整个浏览器后 cookie 失效，下次进入自动清空图片缓存。
const PHOTO_CACHE_DB_NAME = 'detail-photo-cache'
const PHOTO_CACHE_DB_VERSION = 1
const PHOTO_CACHE_STORE_NAME = 'photos'
const PHOTO_CACHE_SESSION_COOKIE = 'photo_cache_session'
const PHOTO_CACHE_LEGACY_PREFIX = 'detail-photo-cache:'

let photoCacheDbPromise: Promise<IDBDatabase | null> | null = null
let photoCacheSessionChecked = false

const getCookie = (name: string): string => {
  try {
    const matched = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`))
    return matched ? decodeURIComponent(matched[1]) : ''
  } catch {
    return ''
  }
}

// 写入浏览器会话标记（不设置过期时间，关闭浏览器后自动失效）
const setPhotoCacheSessionCookie = (value: string) => {
  try {
    document.cookie = `${PHOTO_CACHE_SESSION_COOKIE}=${encodeURIComponent(value)}; path=/; SameSite=Lax`
  } catch {
    // 忽略
  }
}

const openPhotoCacheDb = (): Promise<IDBDatabase | null> => {
  if (photoCacheDbPromise) return photoCacheDbPromise
  photoCacheDbPromise = new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') {
        resolve(null)
        return
      }
      const request = indexedDB.open(PHOTO_CACHE_DB_NAME, PHOTO_CACHE_DB_VERSION)
      request.onupgradeneeded = () => {
        const db = request.result
        if (!db.objectStoreNames.contains(PHOTO_CACHE_STORE_NAME)) {
          db.createObjectStore(PHOTO_CACHE_STORE_NAME)
        }
      }
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => resolve(null)
      request.onblocked = () => resolve(null)
    } catch {
      resolve(null)
    }
  })
  return photoCacheDbPromise
}

// 清空图片缓存（IndexedDB 主存储 + 兼容清理旧版 localStorage/sessionStorage 缓存）
const clearAllCachedPhotos = async () => {
  const db = await openPhotoCacheDb()
  if (db) {
    await new Promise<void>((resolve) => {
      try {
        const transaction = db.transaction(PHOTO_CACHE_STORE_NAME, 'readwrite')
        transaction.objectStore(PHOTO_CACHE_STORE_NAME).clear()
        transaction.oncomplete = () => resolve()
        transaction.onerror = () => resolve()
        transaction.onabort = () => resolve()
      } catch {
        resolve()
      }
    })
  }
  try {
    const localKeys: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(PHOTO_CACHE_LEGACY_PREFIX)) localKeys.push(key)
    }
    localKeys.forEach((key) => localStorage.removeItem(key))
    const sessionKeys: string[] = []
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i)
      if (key && key.startsWith(PHOTO_CACHE_LEGACY_PREFIX)) sessionKeys.push(key)
    }
    sessionKeys.forEach((key) => sessionStorage.removeItem(key))
  } catch {
    // 忽略
  }
}

// 保证处于同一个浏览器会话：
// - cookie 存在 => 同一浏览器会话（关过页签也算），保留图片缓存；
// - cookie 不存在 => 新浏览器会话（上次关闭了整个浏览器），清空旧图片缓存并重新打标记。
const ensurePhotoCacheSession = async () => {
  if (photoCacheSessionChecked) return
  photoCacheSessionChecked = true
  if (getCookie(PHOTO_CACHE_SESSION_COOKIE)) return
  await clearAllCachedPhotos()
  setPhotoCacheSessionCookie(`${Date.now()}-${Math.random().toString(36).slice(2)}`)
}

// 兼容不同的接口返回形态，取出真正的 Blob
const resolvePhotoBlob = (res: any): Blob | null => {
  if (res instanceof Blob) return res
  if (res?.data instanceof Blob) return res.data
  return null
}

// 读取缓存：命中则直接返回 Blob，无需再请求接口
const getCachedPhotoBlob = async (fileName: string): Promise<Blob | null> => {
  if (!fileName) return null
  await ensurePhotoCacheSession()
  const db = await openPhotoCacheDb()
  if (!db) return null
  return new Promise((resolve) => {
    try {
      const transaction = db.transaction(PHOTO_CACHE_STORE_NAME, 'readonly')
      const request = transaction.objectStore(PHOTO_CACHE_STORE_NAME).get(fileName)
      request.onsuccess = () => {
        const value = request.result
        resolve(value instanceof Blob ? value : null)
      }
      request.onerror = () => resolve(null)
    } catch {
      resolve(null)
    }
  })
}

// 写入缓存：写入失败（配额超限、隐私模式等）不影响页面展示
const setCachedPhotoBlob = async (fileName: string, blob: Blob) => {
  if (!fileName || !blob) return
  await ensurePhotoCacheSession()
  const db = await openPhotoCacheDb()
  if (!db) return
  await new Promise<void>((resolve) => {
    try {
      const transaction = db.transaction(PHOTO_CACHE_STORE_NAME, 'readwrite')
      transaction.objectStore(PHOTO_CACHE_STORE_NAME).put(blob, fileName)
      transaction.oncomplete = () => resolve()
      transaction.onerror = () => resolve()
      transaction.onabort = () => resolve()
    } catch {
      resolve()
    }
  })
}

// ========== 图片加载性能优化 ==========
// 1) 并发池：同时最多加载 4 张，避免大量请求 + 图片解码占满主线程导致点击无响应
const createLimitedRunner = (maxConcurrent: number) => {
  let active = 0
  const queue: Array<() => void> = []
  const next = () => {
    if (active >= maxConcurrent) return
    const run = queue.shift()
    if (!run) return
    active += 1
    run()
  }
  return <T>(task: () => Promise<T>): Promise<T> =>
    new Promise<T>((resolve, reject) => {
      queue.push(() => {
        task()
          .then(resolve, reject)
          .finally(() => {
            active -= 1
            next()
          })
      })
      next()
    })
}
const runWithImageLimit = createLimitedRunner(4)

// 2) 同一帧内合并多张图片的更新，避免"每加载完一张就整页重渲染"
type ImageMapRef = typeof remarkImagesByMainTable | typeof optionExampleImagesByMainTable
interface PendingImagePatch {
  imageMapRef: ImageMapRef
  mainTableId: string
  fileName: string
  patch: Partial<DetailImageItem>
}
const imagePendingPatches = new Map<string, PendingImagePatch>()
let imageFlushScheduled = false

// 3) 取消令牌：切换记录 / 返回 / 卸载时让进行中的图片任务立即失效
let imageLoadEpoch = 0
const cancelImageLoading = () => {
  imageLoadEpoch += 1
  imagePendingPatches.clear()
}

// 4) 就地修改图片项（不再复制整个 Map / 数组）
const applyImagePatch = ({ imageMapRef, mainTableId, fileName, patch }: PendingImagePatch) => {
  const images = imageMapRef.value.get(mainTableId)
  if (!images) return
  const image = images.find((item) => item.fileName === fileName)
  if (!image) return
  if (image.url?.startsWith('blob:') && patch.url && patch.url !== image.url) {
    URL.revokeObjectURL(image.url)
  }
  Object.assign(image, patch)
}

const flushImagePatches = () => {
  imageFlushScheduled = false
  const pending = Array.from(imagePendingPatches.values())
  imagePendingPatches.clear()
  pending.forEach(applyImagePatch)
}

const replaceImageItem = (
  imageMapRef: ImageMapRef,
  mainTableId: string,
  fileName: string,
  patch: Partial<DetailImageItem>
) => {
  const kind = imageMapRef === remarkImagesByMainTable ? 'remark' : 'option'
  imagePendingPatches.set(`${kind}:${mainTableId}:${fileName}`, {
    imageMapRef,
    mainTableId,
    fileName,
    patch
  })
  if (imageFlushScheduled) return
  imageFlushScheduled = true
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(flushImagePatches)
  } else {
    setTimeout(flushImagePatches, 16)
  }
}

const loadRemarkImages = (imagesByMainTable: Map<string, RemarkImageSource[]>): Promise<void> => {
  if (!imagesByMainTable || imagesByMainTable.size === 0) {
    remarkImagesByMainTable.value = new Map()
    remarkImagesLoadingPromise = Promise.resolve()
    return remarkImagesLoadingPromise
  }

  // 记录本次加载的版本，切页 / 返回后旧任务自动失效
  const epoch = imageLoadEpoch
  const isStale = () => epoch !== imageLoadEpoch

  const resultMap = new Map<string, DetailImageItem[]>()
  const tasks: Promise<void>[] = []
  
  for (const [mainTableId, imageList] of imagesByMainTable.entries()) {
    const images = imageList.map((image) => ({
      fileName: image.fileName,
      remark: image.remark,
      url: '',
      loading: true,
      error: false
    }))

    if (images.length > 0) {
      resultMap.set(mainTableId, images)
    }

    imageList.forEach(({ fileName }) => {
      tasks.push(
        runWithImageLimit(async () => {
          if (isStale()) return
          // 1. 命中本地缓存则直接使用，不再请求接口
          const cachedBlob = await getCachedPhotoBlob(fileName)
          if (isStale()) return
          if (cachedBlob) {
            replaceImageItem(remarkImagesByMainTable, mainTableId, fileName, {
              url: window.URL.createObjectURL(cachedBlob),
              loading: false,
              error: false
            })
            return
          }

          // 2. 无缓存则请求接口，成功后写入本地缓存（关闭浏览器自动清空）
          try {
            const res: any = await list.downloadPhoto({ 文件名: fileName })
            if (isStale()) return
            const blob = resolvePhotoBlob(res)
            if (!blob) {
              throw new Error('图片数据格式不正确')
            }
            replaceImageItem(remarkImagesByMainTable, mainTableId, fileName, {
              url: window.URL.createObjectURL(blob),
              loading: false,
              error: false
            })
            await setCachedPhotoBlob(fileName, blob)
          } catch (error) {
            if (isStale()) return
            console.error(`加载图片失败: ${fileName}`, error)
            replaceImageItem(remarkImagesByMainTable, mainTableId, fileName, {
              url: '',
              loading: false,
              error: true
            })
          }
        })
      )
    })
  }
  
  remarkImagesByMainTable.value = resultMap
  remarkImagesLoadingPromise = Promise.allSettled(tasks).then(() => undefined)
  return remarkImagesLoadingPromise
}

const clearOptionExampleImages = () => {
  for (const images of optionExampleImagesByMainTable.value.values()) {
    for (const image of images) {
      if (image.url?.startsWith('blob:')) {
        URL.revokeObjectURL(image.url)
      }
    }
  }
  optionExampleImagesByMainTable.value = new Map()
}

// 释放现场照片的 blob URL（IndexedDB 中的缓存不受影响）
const clearRemarkImages = () => {
  for (const images of remarkImagesByMainTable.value.values()) {
    for (const image of images) {
      if (image.url?.startsWith('blob:')) {
        URL.revokeObjectURL(image.url)
      }
    }
  }
  remarkImagesByMainTable.value = new Map()
}

const loadOptionExampleImages = (imagesByMainTable: Map<string, string[]>) => {
  clearOptionExampleImages()
  if (!imagesByMainTable || imagesByMainTable.size === 0) return

  const epoch = imageLoadEpoch
  const isStale = () => epoch !== imageLoadEpoch

  const resultMap = new Map<string, DetailImageItem[]>()
  const tasks: Promise<void>[] = []

  for (const [mainTableId, imageList] of imagesByMainTable.entries()) {
    const images = imageList.map((fileName) => ({
      fileName,
      url: '',
      loading: true,
      error: false
    }))

    if (images.length > 0) {
      resultMap.set(mainTableId, images)
    }

    imageList.forEach((fileName) => {
      tasks.push(
        runWithImageLimit(async () => {
          if (isStale()) return
          try {
            const res = await ParameterSettings.clientDownload({ fileName })
            if (isStale()) return
            const url = parseDownloadResponseToUrl(res)
            if (!url) throw new Error('图片内容为空')
            replaceImageItem(optionExampleImagesByMainTable, mainTableId, fileName, {
              url,
              loading: false,
              error: false
            })
          } catch (error) {
            if (isStale()) return
            console.error(`加载选项示意图失败: ${fileName}`, error)
            replaceImageItem(optionExampleImagesByMainTable, mainTableId, fileName, {
              url: '',
              loading: false,
              error: true
            })
          }
        })
      )
    })
  }

  optionExampleImagesByMainTable.value = resultMap
  Promise.allSettled(tasks)
}

const addUniqueImageFile = (imageList: string[], fileName: string) => {
  const normalized = toNonEmptyString(fileName)
  if (normalized && !imageList.includes(normalized)) {
    imageList.push(normalized)
  }
}

const parseOptionWithImage = (option: unknown): { label: string; fileName: string } | null => {
  const optionText = toNonEmptyString(option)
  if (!optionText) return null

  const [label, fileName] = optionText.split('|||')
  const normalizedLabel = toNonEmptyString(label)
  const normalizedFileName = toNonEmptyString(fileName)

  if (!normalizedLabel || !normalizedFileName) return null
  return { label: normalizedLabel, fileName: normalizedFileName }
}

const splitSelectedOptionValues = (value: unknown): string[] => {
  const selectedValue = toNonEmptyString(value)
  if (!selectedValue) return []

  return selectedValue
    .split(/[,，;；、|]/)
    .map((item) => toNonEmptyString(item))
    .filter(Boolean)
}

const collectSelectedOptionImages = (project: any, mainTable: any): string[] => {
  const formatDesc = project?.检测原始数据检测数据格式描述
  const optionColumns = Array.isArray(formatDesc?.选项) ? formatDesc.选项 : []
  const subRows = Array.isArray(mainTable?.检测原始数据详细数据子表集合)
    ? mainTable.检测原始数据详细数据子表集合
    : []

  if (optionColumns.length === 0 || subRows.length === 0) return []

  const selectedImages: string[] = []

  optionColumns.forEach((options: unknown, columnIndex: number) => {
    if (!Array.isArray(options) || options.length === 0) return

    const optionImageMap = new Map<string, string>()
    options.forEach((option) => {
      const parsed = parseOptionWithImage(option)
      if (parsed) {
        optionImageMap.set(parsed.label, parsed.fileName)
      }
    })

    if (optionImageMap.size === 0) return

    const fieldKey = `_${columnIndex + 1}`
    subRows.forEach((row: any) => {
      splitSelectedOptionValues(row?.[fieldKey]).forEach((selectedValue) => {
        const fileName = optionImageMap.get(selectedValue)
        if (fileName) {
          addUniqueImageFile(selectedImages, fileName)
        }
      })
    })
  })

  return selectedImages
}

// 加载详情数据
const loadDetailData = async (options?: { restorePosition?: boolean }) => {
  // 切换记录时取消上一轮未完成的图片加载
  cancelImageLoading()
  const shouldRestorePosition = options?.restorePosition ?? true
  // 优先从store获取参数，如果store中没有或已过期，则从query参数获取
  const id = getCurrentDetailId()
  
  if (!id) {
    ElMessage.error('缺少ID参数，正在返回列表页')
    setTimeout(() => {
      const fromPath = detailStore.getFromPath || originalDetectionListPath()
      router.push(fromPath)
    }, 1500)
    return
  }
  
  try {
    loading.value = true

    const res = await getData<{ status: number; success: boolean; response: Record<string, unknown> }>("list/listById", { id }, [])
    
    if (res && res.status === 200 && res.success && res.response) {
    detailData.value = res.response

    // 调试：输出每条主表的单条数据特殊属性（开发联调后可删除）
    const projects = (res.response['检测原始数据检测项目集合'] as any[]) || []
    projects.forEach((project: any) => {
      const mainTables = project['检测原始数据详细数据主表集合'] || []
      mainTables.forEach((mainTable: any) => {
        const attrs = mainTable['检测原始数据检测项目单条数据特殊属性集合']
        if (attrs && attrs.length > 0) {
          console.log('[调试-单条属性] 主表Id:', mainTable.Id || mainTable.id, '属性:', attrs)
        } else {
          console.log('[调试-单条属性] 主表Id:', mainTable.Id || mainTable.id, '无单条属性或为空')
        }
      })
    })

    // 设置页面标题
      const name = res.response['名称'] as string || '原始记录详情'
      setPageTitle(`${name} - 详情`)

      const operatingProjectId = getOperatingProjectId(res.response)
      if (operatingProjectId) {
        await loadOperatingProjectDefinition(operatingProjectId)
      } else {
        operatingProjectDetailId.value = ''
        operatingProjectBuildings.value = []
        clearOperatingProjectFileCache()
      }
      
      // 从检测原始数据检测项目集合中查找备注图像列表和选项示意图（按主表分组）
      const imagesByMainTable = new Map<string, RemarkImageSource[]>()
      const optionExampleImagesByMainTable = new Map<string, string[]>()
      
      if (res.response['检测原始数据检测项目集合'] && Array.isArray(res.response['检测原始数据检测项目集合'])) {
        const projects = res.response['检测原始数据检测项目集合'] as any[]
        
        // 遍历所有检测项目
        for (const project of projects) {
          if (project['检测原始数据详细数据主表集合'] && Array.isArray(project['检测原始数据详细数据主表集合'])) {
            const mainTables = project['检测原始数据详细数据主表集合'] as any[]
            
            // 遍历所有主表，收集备注图像列表
            for (const mainTable of mainTables) {
              const mainTableId = String(mainTable.Id || mainTable.id)
              let imageList: RemarkImageSource[] = []
              const optionExampleImage = toNonEmptyString(mainTable['选项示意图'] ?? mainTable['选项示意'])
              const optionImageList: string[] = []
              
              if (mainTable['备注图像列表'] && Array.isArray(mainTable['备注图像列表'])) {
                imageList = parseRemarkImages(mainTable['备注图像列表'])
              } else if (mainTable['备注图像列表json']) {
                try {
                  const parsed = JSON.parse(mainTable['备注图像列表json'])
                  if (Array.isArray(parsed)) {
                    imageList = parseRemarkImages(parsed)
                  }
                } catch (e) {
                  console.error('解析备注图像列表json失败:', e)
                }
              }
              
              if (imageList.length > 0) {
                imagesByMainTable.set(mainTableId, imageList)
              }

              if (optionExampleImage) {
                addUniqueImageFile(optionImageList, optionExampleImage)
              }

              collectSelectedOptionImages(project, mainTable).forEach((fileName) => {
                addUniqueImageFile(optionImageList, fileName)
              })

              if (optionImageList.length > 0) {
                optionExampleImagesByMainTable.set(mainTableId, optionImageList)
              }
            }
          }
        }
      }
      
      if (imagesByMainTable.size > 0) {
        remarkImagesLoadingPromise = loadRemarkImages(imagesByMainTable)
      } else {
        remarkImagesByMainTable.value = new Map()
        remarkImagesLoadingPromise = Promise.resolve()
      }

      loadOptionExampleImages(optionExampleImagesByMainTable)

      ElMessage.success('数据加载成功')
    } else {
      ElMessage.error('数据加载失败')
    }
  } catch (error) {
    console.error('加载详情数据失败:', error)
    ElMessage.error('数据加载失败')
  } finally {
    loading.value = false
  }

  if (shouldRestorePosition) {
    await restoreChangePositionIfNeeded(id)
  }
}

// 组件挂载时加载数据
onMounted(() => {
  setPageTitle('原始记录详情')
  loadDetailData({ restorePosition: true })
})

onBeforeUnmount(() => {
  // 组件卸载后不再继续加载图片，避免无效任务占用主线程
  cancelImageLoading()
  clearOperatingProjectFileCache()
  clearOptionExampleImages()
  clearRemarkImages()
})
</script>

<style scoped>
.details-page {
  max-width: 1200px;
  margin: 0 auto;
}

.back-button {
  margin-right: 8px;
}

.loading-container {
  padding: 20px;
}

.detail-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.drawing-entry-sticky {
  position: sticky;
  top: 160px;
  align-self: flex-end;
  z-index: 20;
  margin-top: -76px;
}

.drawing-entry-btn {
  width: 54px;
  height: 54px;
  box-shadow: 0 8px 20px rgba(64, 158, 255, 0.25);
}

.operating-project-dialog {
  min-height: 360px;
}

.op-building-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.op-building-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  margin-bottom: 16px;
  color: #606266;
  font-size: 14px;
}

.op-drawing-layout {
  display: flex;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  min-height: 380px;
  overflow: hidden;
}

.op-floor-list {
  width: 140px;
  border-right: 1px solid #ebeef5;
  background: #fafafa;
  overflow: auto;
}

.op-floor-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 12px 10px;
  border-bottom: 1px solid #f0f2f5;
  cursor: pointer;
  transition: background-color 0.2s;
}

.op-floor-item.active {
  background: #ecf5ff;
  color: #409eff;
  font-weight: 600;
}

.op-drawing-panel {
  flex: 1;
  padding: 16px;
  overflow: auto;
}

.op-drawing-panel h4 {
  margin: 0 0 12px 0;
  font-size: 18px;
}

.op-drawing-code {
  margin: 0 0 16px 0;
  color: #909399;
  font-size: 13px;
  word-break: break-all;
}

.op-image-preview,
.op-pdf-preview {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
}

.op-floor-image {
  width: 100%;
  height: min(60vh, 560px);
  max-height: 560px;
  border: 1px dashed #dcdfe6;
  border-radius: 6px;
  background-color: #f8fafc;
}

.op-floor-image :deep(.el-image__inner) {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.op-error-text {
  margin: 0;
  color: #f56c6c;
  font-size: 13px;
}

.info-card {
  margin-bottom: 20px;
}

/* 基本信息 Tag 样式 */
.info-tags-container {
  width: 100%;
}

.info-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.info-tag {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.info-tag:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.tag-label {
  font-weight: 600;
  margin-right: 6px;
  opacity: 0.9;
}

.tag-value {
  font-weight: 500;
  word-break: break-all;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .drawing-entry-sticky {
    top: 120px;
    margin-top: 0;
  }

  .drawing-entry-btn {
    width: 46px;
    height: 46px;
  }

  .op-drawing-layout {
    flex-direction: column;
  }

  .op-floor-list {
    width: 100%;
    max-height: 180px;
    border-right: none;
    border-bottom: 1px solid #ebeef5;
  }

  .info-tags {
    flex-direction: column;
    align-items: stretch;
  }
  
  .info-tag {
    width: 100%;
    justify-content: space-between;
  }
  
  .tag-value {
    max-width: none;
    text-align: right;
  }
}

.projects-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.project-card {
  margin-bottom: 20px;
}

.project-info {
  margin-bottom: 20px;
}

.special-attributes {
  margin-bottom: 20px;
}

.special-attributes h4 {
  margin: 0 0 12px 0;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}

.special-attributes-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.attribute-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.attribute-label {
  min-width: 120px;
  font-weight: 500;
  color: #606266;
  font-size: 14px;
  flex-shrink: 0;
}

.attribute-input {
  width: 200px;
  flex-shrink: 0;
}

.attribute-input :deep(.el-input__wrapper) {
  background-color: #f5f7fa;
  border: 1px solid #e4e7ed;
  box-shadow: none;
  cursor: default;
}

.attribute-input :deep(.el-input__inner) {
  color: #606266;
  cursor: default;
}


.detailed-data {
  margin-bottom: 20px;
}

.detailed-data h4 {
  margin: 0 0 12px 0;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}

.data-table-container {
  margin-bottom: 24px;
  scroll-margin-top: 96px;
}

.data-table {
  margin-bottom: 0;
}

/* 单条数据特殊属性 */
.single-special-attrs {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 10px 0 14px;
  padding: 0;
  background: transparent;
  border: none;
}

.single-special-attr-prefix {
  font-size: 13px;
  color: #606266;
  font-weight: 500;
  white-space: nowrap;
}

.single-special-attr-tag {
  font-size: 13px;
}

/* 分区线样式 */
.data-table :deep(.divider-column) {
  border-right: 3px solid #409eff !important;
}

.data-table :deep(.el-table__header .divider-column) {
  border-right: 3px solid #409eff !important;
}

.table-summary-header {
  padding: 12px 0;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.table-title {
  font-weight: 600;
  color: #303133;
  font-size: 14px;
  flex-shrink: 0;
}

.table-summary {
  font-size: 13px;
  color: #606266;
  line-height: 1.4;
  flex: 0 1 auto;
  min-width: 0;
}

.option-example-images {
  max-width: 360px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-start;
  flex: 0 0 auto;
}

.option-example-image {
  width: 96px;
  height: 72px;
  flex: 0 0 96px;
}

.option-example-thumb {
  width: 96px;
  height: 72px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #f5f7fa;
  cursor: zoom-in;
}

.option-example-thumb :deep(.el-image__inner) {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.option-example-placeholder {
  width: 96px;
  height: 72px;
  border: 1px dashed #dcdfe6;
  border-radius: 4px;
  background: #f5f7fa;
  color: #909399;
  font-size: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.table-summary-actions {
  margin-left: auto;
  flex-shrink: 0;
}

.raw-data-change-dialog-body {
  min-height: 220px;
}

.raw-data-change-meta {
  margin-bottom: 16px;
  color: #606266;
  font-size: 13px;
}

.raw-data-change-form {
  margin-bottom: 16px;
}

.raw-data-change-form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  column-gap: 16px;
}

.raw-data-change-json-input :deep(textarea) {
  font-family: Consolas, Monaco, 'Courier New', monospace;
}

.raw-data-change-number-input {
  width: 100%;
}

.raw-data-change-sub-table {
  margin-top: 16px;
}

.raw-data-change-sub-table h4 {
  margin: 0 0 12px 0;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}

/* 无参数提示样式 */
.no-params-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 40px 20px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .details-page {
    padding: 12px;
  }
  
  .info-grid {
    grid-template-columns: 1fr;
  }
  
  .info-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
  
  .info-item label {
    min-width: auto;
  }
  
  .format-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
  
  .format-item label {
    min-width: auto;
  }
  
  .attribute-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .attribute-label {
    min-width: auto;
    width: 100%;
  }
  
  .attribute-input {
    width: 200px;
  }
  
  .table-summary-header {
    padding: 8px 0;
    flex-wrap: wrap;
  }
  
  .table-title {
    font-size: 13px;
  }
  
  .table-summary {
    font-size: 12px;
    flex-basis: 100%;
  }

  .option-example-image,
  .option-example-thumb,
  .option-example-placeholder {
    width: 80px;
    height: 60px;
    flex-basis: 80px;
  }

  .table-summary-actions {
    margin-left: 0;
  }

  .raw-data-change-form-grid {
    grid-template-columns: 1fr;
  }
}

.remark-image-preview-dialog {
  display: flex;
  flex-direction: column;
  min-height: 420px;
}

.remark-image-preview-stage {
  position: relative;
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  justify-content: center;
  min-height: 360px;
  padding: 16px 64px;
  background: #f5f7fa;
}

.remark-image-preview-image {
  display: block;
  max-width: 100%;
  max-height: min(64vh, 640px);
  object-fit: contain;
}

.remark-preview-nav {
  position: absolute;
  z-index: 1;
}

.remark-preview-nav--previous {
  left: 16px;
}

.remark-preview-nav--next {
  right: 16px;
}

.remark-image-preview-note {
  position: sticky;
  bottom: 0;
  display: flex;
  flex: 0 0 auto;
  gap: 12px;
  min-height: 52px;
  padding: 14px 16px;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-top: 0;
  line-height: 24px;
}

.remark-image-preview-note-label {
  flex: 0 0 auto;
  color: #606266;
  font-weight: 600;
}

.remark-image-preview-note-value {
  min-width: 0;
  color: #303133;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

/* 主表内的现场照片样式 */
.main-table-images {
  margin-top: 16px;
  padding-top: 16px;
  padding-bottom: 16px;
  border-bottom: 2px solid #303133;
}

.main-table-images h5 {
  margin: 0 0 12px 0;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}

.images-grid-small {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 4px 0;
}

.image-item-small {
  position: relative;
  width: 112px;
  flex-shrink: 0;
  cursor: pointer;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
  transition: all 0.2s ease;
}

.image-item-small:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.image-thumb-wrapper {
  position: relative;
  width: 100%;
  height: 112px;
  overflow: hidden;
}

.thumbnail-image-small {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.thumbnail-image-small :deep(.el-image__inner) {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 缩略图上的备注角标：无需点开图片即可看到备注 */
.image-remark-badge {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 5px;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.15) 0%, rgba(0, 0, 0, 0.78) 100%);
  color: #fff;
  font-size: 11px;
  line-height: 1.3;
}

.image-remark-badge-label {
  flex-shrink: 0;
  padding: 0 4px;
  border-radius: 3px;
  background: #f56c6c;
  font-size: 10px;
  font-weight: 600;
}

.image-remark-badge-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.image-error-small {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  background-color: #f5f7fa;
  color: #909399;
  font-size: 10px;
}

.image-error-small .el-icon {
  font-size: 24px;
}

.image-loading-small {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  background-color: #f5f7fa;
  color: #409eff;
}

.image-loading-small .el-icon {
  font-size: 24px;
}

.image-error-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #f5f7fa;
  color: #909399;
  font-size: 10px;
  border-radius: 4px;
}

.image-error-placeholder .el-icon {
  font-size: 24px;
  margin-bottom: 4px;
}

.image-error-placeholder span {
  font-size: 10px;
}

@media (max-width: 768px) {
  .images-grid-small {
    gap: 6px;
  }
  
  .image-item-small {
    width: 88px;
  }

  .image-thumb-wrapper {
    height: 88px;
  }

  .image-remark-badge {
    font-size: 10px;
  }

  .op-floor-image {
    height: min(45vh, 320px);
    max-height: 320px;
  }
}
</style>
